/**
 * Route auth for acequia peer routes — the one verifier for a bearer
 * presented to a route (unified-token-system, Phase 3).
 *
 * A route's `metadata.auth` wraps its handler in createAuthMiddleware. The
 * bearer (`Authorization: Bearer …` or `?token=`) is an id or a JWT, and
 * verifyBearer takes every kind this library mints:
 *
 *   peer token   a record in acequia.peerTokens (by id, or the JWT it holds):
 *                its status, a lapsed one tolerated when marked auto-renew,
 *                the signature by its signer's key, a working exp, the audience
 *   grant        an anchor-model leaf (acequia.grants): the chain to its anchor
 *                resolved locally (the wallet, the link cache, the anchors
 *                presented with it) and verified — signature, scope within the
 *                anchor's, the anchor's revocation — through resolveAndVerify
 *   chain        a legacy parent-linked leaf — refused (the dual-accept
 *                window closed 2026-10-06; legacy chains verify nowhere)
 *   user token   a token a user signed for themselves (kid, no anchor): the
 *                signature and exp; its scope is what it claims — empty when
 *                it claims none, which opens only routes that require none
 *                (the grant manager's owner-only routes)
 *
 * An anchor is never a credential (typ: anchor is refused before anything is
 * resolved). Self-rooted: a route accepts only tokens rooted in the user of
 * the browser serving it — this browser's user, or one it was before linking
 * to an account (auth/identities.js). Peer grants are minted by the serving
 * browser's user, as the BrowserDAV engine accepts only its owner's chains.
 * Without this a registered user of the domain could sign their own
 * full-scope token and open any route (closed 2026-10-02). `anyIssuer: true`
 * opts a route out.
 *
 * Scope: every decision goes through auth/chains' checkScopeAccess (the one
 * method → privilege table, shared with the server), over the token's
 * canonical grant set. A route's declared `scope`, when it is a grant set or
 * a legacy { paths, writePaths }, is a requirement the token must cover
 * (isScopeSubset); a string is a label only (an app's preset name), so the
 * check is what the method needs on the path.
 */

import { verifyPeerToken, scopeGrantSet, getJwtById, getPeerToken, audienceAllows } from '../auth/peerTokens.js'
import { canonicalizeScope, checkScopeAccess, isScopeSubset, isPresentedAnchor } from '../auth/chains.js'
import { jose } from '../deps.js'

// ── scope decisions ───────────────────────────────────────────────────

/**
 * A token's grants in two sets: relative patterns ('geo/*', '*') name a
 * route inside any instance or camera; rooted ones ('/<id>/*') name one
 * resource and match under the route's auth.root (geocamera-multicamera,
 * phase 4). A grant with conditions is left out (fails closed): route
 * requests carry no body the conditions could be judged on.
 */
function splitGrants(set) {
    const relative = [], rooted = []
    for (const g of canonicalizeScope(set).grants) {
        if (g.conditions && Object.keys(g.conditions).length) continue
        ;(g.pattern.startsWith('/') ? rooted : relative).push(g)
    }
    return { relative: { grants: relative }, rooted: { grants: rooted } }
}

const rootedPath = (root, relPath) => `${root.replace(/\/+$/, '')}/${relPath}`.replace(/\/+$/, '')

/**
 * Whether a token scope allows a method on a route path.
 * @param {object|string} scope - token scope: grant triples, legacy { paths, writePaths },
 *   or a legacy name (healed, logged LEGACY_SCOPE_HEAL; an unknown name allows nothing)
 * @param {string} method
 * @param {string} relPath - route path without its first segment ('geo/status')
 * @param {string} [root] - the route's auth.root ('/camABC'); without one, a rooted grant matches nothing
 */
export function scopeAllows(scope, method, relPath, root) {
    const set = scopeGrantSet(scope, 'route').scope
    if (!set) return false
    const m = String(method || 'GET').toUpperCase()
    const { relative, rooted } = splitGrants(set)
    if (relative.grants.length && checkScopeAccess(m, relPath, relative, { ancestors: false }).allowed) return true
    if (root && rooted.grants.length) return checkScopeAccess(m, rootedPath(root, relPath), rooted, { ancestors: false }).allowed
    return false
}

/**
 * Whether a token scope covers a route's declared scope (every grant the
 * route requires is within one the token holds — isScopeSubset). The
 * declared patterns are route-relative; against a rooted token they are
 * read under the route's root.
 * @param {object|string} tokenScope
 * @param {object} requiredScope - a grant set or legacy { paths, writePaths }
 * @param {string} [root]
 */
export function scopeCovers(tokenScope, requiredScope, root) {
    const set = scopeGrantSet(tokenScope, 'route').scope
    if (!set) return false
    const required = canonicalizeScope(requiredScope)
    if (!required.grants.length) return true
    const { relative, rooted } = splitGrants(set)
    if (isScopeSubset(required, relative)) return true
    if (!root) return false
    const base = root.replace(/\/+$/, '')
    const underRoot = { grants: required.grants.map((g) => ({ ...g, pattern: g.pattern === '*' ? `${base}/*` : `${base}/${g.pattern}` })) }
    return isScopeSubset(underRoot, rooted)
}

// ── the bearer ────────────────────────────────────────────────────────

/**
 * This browser's user ids: the current user, and any it was before linking
 * to an account (auth/identities.js). Loaded lazily (acequia.js imports this
 * module).
 */
async function localUserIds() {
    try {
        const { ownUserIds } = await import('../auth/identities.js')
        return await ownUserIds()
    } catch {
        try {
            const { getUserId } = await import('./acequia.js')
            return [getUserId()].filter(Boolean)
        } catch {
            return []
        }
    }
}

function decode(jwt) {
    try {
        return { claims: jose.decodeJwt(jwt), header: jose.decodeProtectedHeader(jwt) }
    } catch {
        return null
    }
}

/**
 * Verify a bearer presented to a route: an id or a JWT, of any kind this
 * library mints (see the module comment).
 *
 * @param {string} bearer
 * @param {object} [opts]
 * @param {string} [opts.aud] - the app the route belongs to; a token for another is refused,
 *   one without an audience passes, logged (the heal, until the sunset)
 * @param {boolean} [opts.anyIssuer=false] - accept tokens rooted in users other than this browser's
 * @param {string[]} [opts.owners] - the users a token must be rooted in, instead of this browser's
 *   (a BrowserDAV server with a declared owner, on a page that has no user of its own)
 * @param {string[]} [opts.anchors] - anchors presented with the bearer (a grant this browser has
 *   no record of verifies against them)
 * @param {string} [opts.authUrl] - the auth server, tried last for keys and links
 * @returns {Promise<{ valid: boolean, kind?: 'peer'|'grant'|'chain'|'user', claims?: object,
 *   record?: object, scope?: object, rootSub?: string, lapsed?: boolean, reason?: string,
 *   keyUnresolved?: boolean, revocationCheck?: 'confirmed'|'degraded' }>}
 */
export async function verifyBearer(bearer, { aud, anyIssuer = false, owners, anchors = [], authUrl } = {}) {
    if (!bearer || typeof bearer !== 'string') return { valid: false, reason: 'no token' }

    // An id: a peer-token record here, else a delegation minted here (its
    // wallet row). The id is a lookup key on this device, never a credential
    // elsewhere — nothing is fetched for it
    let jwt = bearer
    let record = null
    if (!bearer.includes('.')) {
        record = await getPeerToken(bearer)
        if (record) {
            jwt = record.jwt
        } else {
            const { rowByLeafId } = await import('../auth/grants.js')
            const row = await rowByLeafId(bearer).catch(() => null)
            if (!row) return { valid: false, reason: 'token not found' }
            if (row.status === 'revoked') return { valid: false, reason: 'revoked' }
            jwt = row.leafJwt
        }
    }

    const decoded = decode(jwt)
    if (!decoded) return { valid: false, reason: 'malformed JWT' }
    const { claims, header } = decoded
    if (header.typ === 'anchor' || isPresentedAnchor(jwt)) return { valid: false, reason: 'anchor is not presentable', claims }

    // A record's own checks first: its status, a lapsed leaf tolerated on
    // auto-renew, the signature, exp, the audience, the stored scope
    let peer = null
    if (record) {
        peer = await verifyPeerToken(bearer.includes('.') ? jwt : bearer, { aud })
        if (!peer.valid) return { ...peer, record }
    }

    // A legacy parent-linked leaf is refused outright: the dual-accept window
    // closed 2026-10-06 (close-the-dual-accept-window) — legacy chains verify
    // nowhere, so there is nothing to resolve.
    if (claims.parent !== undefined) {
        return { valid: false, kind: 'chain', claims, record, reason: 'legacy parent-linked chain tokens are no longer accepted (the dual-accept window closed 2026-10-06) — redeem your short code or ask the grantor for a new delegation' }
    }

    // A grant: an anchor-model leaf (this library's, GeoCamera's, the
    // console's, a BrowserDAV delegation), resolved locally and verified link
    // by link, rooted in this browser's user unless the route takes any issuer
    if (claims.anchor) {
        const { verifyGrant } = await import('../auth/grants.js')
        const g = await verifyGrant(jwt, { anchors, aud, authUrl, ...(anyIssuer ? { rootedIn: null } : owners ? { rootedIn: owners } : {}) })
        if (g.valid) return { valid: true, kind: 'grant', claims: g.claims || claims, record, scope: g.scope, rootSub: g.rootSub, revocationCheck: g.revocationCheck }
        // A lapsed leaf on a grant marked auto-renew passes, as a peer token's
        // does: its minter renews it. Its scope is the record's
        if (peer && g.expired && record?.autoRenew) {
            return { valid: true, kind: 'grant', claims, record, scope: peer.scope, rootSub: claims.sub, lapsed: true }
        }
        return { valid: false, kind: 'grant', claims, record, reason: g.error || 'invalid grant', keyUnresolved: /key/i.test(g.error || '') }
    }

    // A peer token without an anchor (from before acequia.grants), or a user
    // token: the signature by the signer's key, exp, the audience, the scope
    if (!peer) peer = await verifyPeerToken(jwt, { aud })
    if (!peer.valid) return { ...peer, record }
    if (!anyIssuer) {
        const roots = owners || await localUserIds()
        if (!roots.length || !roots.includes(peer.claims?.sub)) {
            return { valid: false, kind: record ? 'peer' : 'user', claims: peer.claims, record, reason: 'token issued by another user' }
        }
    }
    return { valid: true, kind: record ? 'peer' : 'user', claims: peer.claims, record, scope: peer.scope, rootSub: peer.claims?.sub }
}

// ── the request ───────────────────────────────────────────────────────

/**
 * Extract a token from a proxied request's data object.
 * Checks Authorization header, then ?token= query param.
 * @param {object} data - The request data (path, headers, etc.)
 * @returns {string|null} JWT string or CUID, or null if none found
 */
export function extractToken(data) {
    // 1. Authorization header
    const authHeader = data.headers?.['authorization'] || data.headers?.['Authorization']
    if (authHeader) {
        const match = authHeader.match(/^Bearer\s+(.+)$/i)
        if (match) return match[1]
    }

    // 2. ?token= query parameter (may be in path or in a query field)
    if (data.query?.token) return data.query.token

    // Parse from path if it contains a query string
    const path = data.path || ''
    const qIdx = path.indexOf('?')
    if (qIdx !== -1) {
        const params = new URLSearchParams(path.slice(qIdx + 1))
        const token = params.get('token')
        if (token) return token
    }

    return null
}

/**
 * Strip the instanceId prefix from a route path.
 * Routes are registered as /{instanceId}/geo/location but scope
 * is relative (e.g., 'geo/*'). This extracts the part after the
 * first path segment.
 * @param {string} path - Full route path
 * @returns {string} Path without instanceId prefix
 */
function stripInstancePrefix(path) {
    // /x4pw0b9w.../geo/location → geo/location
    const parts = path.replace(/^\//, '').split('/')
    if (parts.length > 1) {
        return parts.slice(1).join('/')
    }
    return path.replace(/^\//, '')
}

/** The anchors a request carries for its bearer, if any (a JSON body with `anchors`). */
function anchorsIn(data) {
    const a = data.anchors
    if (Array.isArray(a)) return a.filter((x) => typeof x === 'string')
    return []
}

/**
 * Create an auth-wrapped handler.
 * @param {function} handler - The original route handler
 * @param {object} authOptions
 * @param {boolean} [authOptions.required=true] - Whether auth is required
 * @param {string|object} [authOptions.scope] - What the route needs. A grant set (or legacy
 *   { paths, writePaths }) the token must cover; a string (an app's preset name) is a label,
 *   and the check is what the method needs on the path. Either way the method is checked
 *   against the token's scope on the route's path
 * @param {boolean} [authOptions.enforce=true] - If false, log warnings but allow through
 * @param {function} [authOptions.verify] - Custom verification function (jwt) => { valid, claims, scope?, reason? }
 * @param {string} [authOptions.root] - the route's grant root ('/<cameraId>'): rooted grants match under it
 * @param {string} [authOptions.aud] - the app the route belongs to: a token for another app is
 *   refused (unified-token-system, 2026-09-23); a token without an audience passes, logged
 * @param {boolean} [authOptions.anyIssuer=false] - accept tokens issued by users other than
 *   this browser's (off: a route takes only tokens its own user issued)
 * @returns {function} Wrapped handler
 */
export function createAuthMiddleware(handler, authOptions = {}) {
    const {
        required = true,
        scope: requiredScope,
        enforce = true,
        verify: customVerify,
        root = null,
        aud = null,
        anyIssuer = false,
    } = authOptions

    const refuse = (status, error) => ({ status, headers: { 'content-type': 'application/json' }, body: JSON.stringify({ error }) })

    return async function authWrappedHandler(data) {
        const rawToken = extractToken(data)

        if (!rawToken) {
            if (!required) return handler(data)
            if (!enforce) {
                console.warn(`[routeAuth] no token for ${data.path} (enforce=false, allowing)`)
                return handler(data)
            }
            return refuse(401, 'Token required')
        }

        // Verify: the one verifier, or the route's own
        let verification
        let jwt = rawToken
        const tokenId = rawToken.includes('.') ? null : rawToken
        if (customVerify) {
            if (tokenId) {
                const resolved = await getJwtById(rawToken)
                if (!resolved) {
                    if (!enforce) {
                        console.warn(`[routeAuth] CUID ${rawToken.slice(0, 8)} not found (enforce=false, allowing)`)
                        return handler(data)
                    }
                    return refuse(401, 'Token not found')
                }
                jwt = resolved
            }
            verification = await customVerify(jwt)
            if (verification.valid && verification.scope === undefined) {
                verification.scope = scopeGrantSet(verification.claims?.scope, 'route').scope
            }
        } else {
            verification = await verifyBearer(rawToken, { aud, anyIssuer, anchors: anchorsIn(data) })
        }

        if (!verification.valid) {
            if (!enforce) {
                console.warn(`[routeAuth] token invalid for ${data.path}: ${verification.reason} (enforce=false, allowing)`)
                return handler(data)
            }
            // An enforced rejection is logged: silence is how the pre-2026-08-20
            // gaps went unnoticed, and a signer key that cannot be resolved
            // (offline, or an unknown peer) is only visible here
            console.warn(`[routeAuth] denied ${data.path}: ${verification.reason}${verification.keyUnresolved ? ' (signer key could not be resolved — offline or unknown peer)' : ''}`)
            return refuse(403, verification.reason || 'Invalid token')
        }

        // Scope: what the method needs on the path, and what the route declares
        if (requiredScope) {
            const routePath = stripInstancePrefix((data.path || '').split('?')[0])
            const method = (data.method || 'GET').toUpperCase()
            const tokenScope = verification.scope
            const declared = typeof requiredScope === 'object' ? requiredScope : null
            const allowed = scopeAllows(tokenScope, method, routePath, root) && (!declared || scopeCovers(tokenScope, declared, root))
            if (!allowed) {
                if (!enforce) {
                    console.warn(`[routeAuth] scope mismatch for ${routePath} (enforce=false, allowing)`)
                    return handler(data)
                }
                return refuse(403, 'Insufficient scope')
            }
        }

        // Auth passed — call handler with auth context
        data.auth = {
            token: verification.record?.jwt || jwt,
            tokenId,
            kind: verification.kind || null,
            claims: verification.claims,
            scope: verification.scope ?? scopeGrantSet(verification.record?.scope || verification.claims?.scope, 'route').scope,
            rootSub: verification.rootSub || verification.claims?.sub || null,
            lapsed: !!verification.lapsed,
        }

        return handler(data)
    }
}
